import type { ReactElement, ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { BlockedState } from "@swot/base-components";
import type { PermissionContext } from "@swot/swot-components";
import { buildPermissionContext } from "@cmts/auth/permissionBridge.js";
import { readCmtsSession } from "@cmts/auth/authStore.js";
import { bootstrapAuthenticatedSession } from "@cmts/auth/authBootstrap.js";
import type { CmtsHostRuntime } from "../../../app/appRuntime.js";
import { CmtsAppProviders } from "../../../app/appProviders.js";
import { EnterpriseShell } from "../../../layouts/EnterpriseShell.js";
import { CmtsNetworkStatusBanner } from "../../../pwa/networkStatus.js";
import { CmtsPwaInstallPanel } from "../../../pwa/pwaInstallPrompt.js";
import { CmtsPwaUpdateStatusPanel } from "../../../pwa/pwaUpdateStatus.js";
import { useCmtsDispatch, useCmtsSelector } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { selectAuthState, selectEffectivePermissions, selectRoles } from "@store/selectors.js";
import { CmtsRouteLoadingFallback } from "./CmtsRouteLoadingFallback.js";

export function CmtsRuntimeRouteRoot(props: { readonly runtime: CmtsHostRuntime }): ReactElement {
  const dispatch = useCmtsDispatch();
  useEffect(() => {
    let cancelled = false;
    const storedSession = readCmtsSession();
    dispatch(authActions.sessionHydrated(storedSession));
    if (!storedSession?.accessToken) {
      dispatch(permissionActions.permissionsCleared());
      dispatch(tenantActions.tenantCleared());
      dispatch(authActions.bootstrapUnauthenticated());
      return () => { cancelled = true; };
    }

    dispatch(authActions.loginBootstrapStarted());
    async function restoreAuthenticatedSession(): Promise<void> {
      try {
        const bootstrap = await bootstrapAuthenticatedSession(props.runtime.repositories.auth, storedSession);
        if (cancelled) return;
        if (!bootstrap) {
          dispatch(permissionActions.permissionsCleared());
          dispatch(tenantActions.tenantCleared());
          dispatch(authActions.bootstrapUnauthenticated());
          return;
        }
        dispatch(tenantActions.tenantResolved(bootstrap.tenant));
        dispatch(permissionActions.permissionsLoaded({ permissions: bootstrap.permissions, roles: bootstrap.roles }));
        dispatch(permissionActions.menuLoaded(bootstrap.menu));
        dispatch(authActions.sessionHydrated(bootstrap.session));
        dispatch(authActions.loginBootstrapSucceeded());
      } catch (error: unknown) {
        if (cancelled) return;
        dispatch(permissionActions.permissionsCleared());
        dispatch(tenantActions.tenantCleared());
        dispatch(authActions.loginBootstrapFailed(serializeReduxError(error, "CMTS session bootstrap failed.")));
      }
    }

    void restoreAuthenticatedSession();
    return () => { cancelled = true; };
  }, [dispatch, props.runtime.repositories.auth]);
  return <Outlet />;
}

export function useCmtsReduxPermissionContext(): PermissionContext | null {
  const auth = useCmtsSelector(selectAuthState);
  const permissions = useCmtsSelector(selectEffectivePermissions);
  const roles = useCmtsSelector(selectRoles);
  const uniquePermissions = useMemo(() => Array.from(new Set(permissions.map(String).filter(Boolean))).sort(), [permissions.join("|")]);
  const uniqueRoles = useMemo(() => Array.from(new Set(roles.map(String).filter(Boolean))).sort(), [roles.join("|")]);
  return useMemo(() => {
    if (auth.bootstrapStatus !== "authenticated" || !auth.session) return null;
    return buildPermissionContext({
      userId: auth.session.userId,
      tenantId: auth.session.tenantId,
      permissions: uniquePermissions,
      roles: uniqueRoles,
      siteIds: auth.session.siteIds,
      confidentialityClearance: auth.session.confidentialityClearance?.toLowerCase?.() as PermissionContext["confidentialityClearance"] | undefined,
    });
  }, [auth.bootstrapStatus, auth.session, uniquePermissions, uniqueRoles]);
}

export function CmtsProtectedRouteShell(props: { readonly runtime: CmtsHostRuntime; readonly children: ReactNode }): ReactElement {
  const location = useLocation();
  const auth = useCmtsSelector(selectAuthState);
  const permissionContext = useCmtsReduxPermissionContext();

  if (auth.bootstrapStatus === "restoring" || auth.bootstrapStatus === "idle") {
    return <CmtsRouteLoadingFallback label="Restoring CMTS session, menu and permissions" />;
  }

  if (auth.bootstrapStatus === "failed") {
    return <BlockedState title="CMTS session could not be restored" message={auth.error?.message ?? "Refresh the page or sign in again to restore your CMTS session."} />;
  }

  if (auth.bootstrapStatus !== "authenticated") {
    return <Navigate to="/cmts/login" replace state={{ returnTo: location.pathname }} />;
  }

  return (
    <EnterpriseShell runtime={props.runtime} activePath={location.pathname} permissionContext={permissionContext}>
      <CmtsNetworkStatusBanner />
      <CmtsPwaInstallPanel />
      <CmtsPwaUpdateStatusPanel />
      {props.children}
    </EnterpriseShell>
  );
}
